window.JRS = window.JRS || {};

(function(JRS) {
  'use strict';

  var LOGO_TYPE_DISPLAY = JRS.LOGO_TYPE_DISPLAY;
  var PDP_TYPES = JRS.PDP_TYPES;

  // §6 (2026-08-04) — customer-facing garment attribution on child lines.
  // ONE visible property naming the garment a logo charge applies to; key is
  // a constant pending Hamza's wording ruling. NOT written to _bundle_raw,
  // _bundle, or any numbered parent property (additive, ERP-invisible).
  var APPLIED_TO_CHILD_KEY = 'Applied to';

  /** "Pro RTX Workwear T Shirt (Unisex) - RX151 — Black / M". Round 5 §5:
      NO truncation — the tail carries the style code (RX151/R237), the most
      identifying part; checkout wraps long values fine. */
  function _garmentLabel(title, colour, size) {
    var t = String(title || '').replace(/\s+/g, ' ').trim();
    var variantBits = [];
    if (colour) variantBits.push(String(colour));
    if (size) variantBits.push(String(size));
    return t + (variantBits.length ? ' — ' + variantBits.join(' / ') : '');
  }

  /**
   * Build a complete cart payload from a ProductConfiguration.
   * @param {import('./jrs-v2-types.js').ProductConfiguration} config
   * @returns {import('./jrs-v2-types.js').CartPayload}
   */
  /**
   * Production position-string canonicalisation (reference v1.1 §11.5 /
   * break-risk #5): the live control theme sends "Large back" (lowercase b);
   * an unrecognised spelling silently auto-creates a duplicate position in
   * Tracker. Emit the production string in every Tracker-read field; the
   * render layer re-capitalises for display. Names without a known production
   * divergence pass through untouched.
   */
  var TRACKER_POSITION_NAMES = { 'large back': 'Large back' };
  function _trackerPositionName(name) {
    var key = String(name || '').replace(/\s+/g, ' ').trim().toLowerCase();
    return TRACKER_POSITION_NAMES[key] || name;
  }

  /** Shallow-clone config with canonicalised position names, so every
      downstream emit (numbered props, children, _bundle_raw, keys) inherits
      one spelling from one place — without mutating the caller's object. */
  function _canonicaliseConfig(config) {
    if (!config || !config.customization || !config.customization.positions) return config;
    var out = {};
    for (var k in config) { if (Object.prototype.hasOwnProperty.call(config, k)) out[k] = config[k]; }
    var cz = {};
    for (var k2 in config.customization) { if (Object.prototype.hasOwnProperty.call(config.customization, k2)) cz[k2] = config.customization[k2]; }
    cz.positions = config.customization.positions.map(function (pos) {
      var q = {};
      for (var k3 in pos) { if (Object.prototype.hasOwnProperty.call(pos, k3)) q[k3] = pos[k3]; }
      q.positionName = _trackerPositionName(pos.positionName);
      return q;
    });
    out.customization = cz;
    return out;
  }

  function build(config) {
    config = _canonicaliseConfig(config);
    var validation = validate(config);
    if (!validation.valid) {
      throw new Error('CartPayloadBuilder: Invalid configuration - ' + validation.errors.join('; '));
    }

    if (config.type === PDP_TYPES.BUNDLE) {
      return buildBundlePayload(config);
    }
    return buildStandardPayload(config);
  }

  /**
   * Validate a ProductConfiguration without building payload.
   * @param {import('./jrs-v2-types.js').ProductConfiguration} config
   * @returns {{ valid: boolean, errors: string[] }}
   */
  function validate(config) {
    var errors = [];

    if (!config || typeof config !== 'object') {
      errors.push('Missing config object');
      return { valid: false, errors: errors };
    }

    if (config.type !== PDP_TYPES.STANDARD && config.type !== PDP_TYPES.BUNDLE) {
      errors.push('Invalid type: ' + config.type);
    }

    if (!config.instanceId || typeof config.instanceId !== 'string') {
      errors.push('Missing instanceId');
    }

    if (!config.categories || config.categories.length === 0) {
      errors.push('No categories');
    }

    var cats = config.categories || [];
    for (var ci = 0; ci < cats.length; ci++) {
      var cat = cats[ci];
      if (!cat || !cat.products || cat.products.length === 0) {
        errors.push('Category ' + ci + ' has no products');
        continue;
      }

      for (var pi = 0; pi < cat.products.length; pi++) {
        var prod = cat.products[pi];
        if (!prod || !prod.sizes || prod.sizes.length === 0) {
          errors.push('Category ' + ci + ' product ' + pi + ' has no sizes');
          continue;
        }

        var hasQty = false;
        for (var si = 0; si < prod.sizes.length; si++) {
          var size = prod.sizes[si];
          if (size.quantity > 0) hasQty = true;
          if (size.quantity > 0 && (!size.variantId || size.variantId <= 0)) {
            errors.push('Category ' + ci + ' product ' + pi + ' size ' + si + ' has quantity but no variantId');
          }
        }

        if (!hasQty) {
          errors.push('Category ' + ci + ' product ' + pi + ' has no sizes with quantity > 0');
        }
      }
    }

    if (config.customization) {
      if (!config.customization.positions || config.customization.positions.length === 0) {
        errors.push('Customization exists but has no positions');
      }

      var posArr = config.customization.positions || [];
      for (var pvi = 0; pvi < posArr.length; pvi++) {
        var pos = posArr[pvi];
        var posLabel = 'Position ' + pvi + ' ("' + (pos && pos.positionName ? pos.positionName : '') + '")';

        if (!pos || !pos.positionVariantId || pos.positionVariantId <= 0) {
          errors.push(posLabel + ' has no positionVariantId');
        }
        if (!pos || (pos.applicationMethod !== 'Embroidery' && pos.applicationMethod !== 'Print')) {
          errors.push(posLabel + ' has invalid applicationMethod: ' + (pos ? pos.applicationMethod : ''));
        }

        var logo = pos ? pos.logo : null;
        if (!logo) {
          errors.push(posLabel + ' has no logo');
        } else {
          if (!logo.logoId) {
            errors.push(posLabel + ' logo has no logoId');
          }

          if ((logo.type === 'upload' || logo.type === 'existing') && (!logo.url || logo.url.indexOf('https://') !== 0)) {
            errors.push(posLabel + ' ' + logo.type + ' logo has invalid URL: ' + logo.url);
          }

          if (logo.type === 'text') {
            if (!logo.text) {
              errors.push(posLabel + ' text logo has no text data');
            } else {
              var lines = logo.text.lines || [];
              var hasLine = false;
              for (var tl = 0; tl < lines.length; tl++) {
                if (lines[tl] && String(lines[tl]).replace(/^\s+|\s+$/g, '').length > 0) {
                  hasLine = true;
                  break;
                }
              }
              if (!hasLine) {
                errors.push(posLabel + ' text logo has all empty lines');
              }
            }
          }
        }
      }
    }

    if (config.type === PDP_TYPES.BUNDLE && config.customization) {
      // Doc 2 §9.3 (v0.7) — `_bundle_raw` size NEVER blocks an add-to-cart.
      //
      // The old 5,000-char hard ceiling assumed that was the Shopify line-item-property
      // limit. It is NOT: Shopify accepts ~1MB+ per property value (cart attributes run to
      // ~20MB), verified 2026-07. A real bundle's witness is a few KB — orders of magnitude
      // under the real limit — so blocking on 5,000 rejected perfectly valid large bundles
      // (many garments × positions × sizes, or a bundle-wide position fanned out across
      // categories). We keep a SOFT, non-blocking warn only at a genuinely large size
      // (~100KB — still ~10× under the real limit) as a performance smell, so a runaway
      // witness is still visible in diagnostics without ever failing the purchase.
      var estimatedRaw = JSON.stringify(buildBundleRawFromConfig(config)); // meta-size proxy; _bundle_raw itself is now tiny (§2.2 spec shape)
      if (estimatedRaw.length > 100000 && typeof console !== 'undefined' && console.warn) {
        console.warn('CartPayloadBuilder: _bundle_raw is ' + estimatedRaw.length +
          ' chars — unusually large. Still well under Shopify\'s ~1MB line-item-property ' +
          'limit (add proceeds), but large properties can slow the cart/order pages.');
      }
    }

    return { valid: errors.length === 0, errors: errors };
  }

  /**
   * @param {import('./jrs-v2-types.js').ProductConfiguration} config
   * @returns {import('./jrs-v2-types.js').CartPayload}
   */
  function buildStandardPayload(config) {
    var timestamp = Date.now();
    var items = [];
    var product = config.categories[0].products[0];
    var hasCustomization = !!(config.customization && config.customization.positions && config.customization.positions.length > 0);
    var positions = hasCustomization ? config.customization.positions : [];

    for (var si = 0; si < product.sizes.length; si++) {
      var sizeEntry = product.sizes[si];
      var parentProps = {};

      if (hasCustomization) {
        var uniqueLogoIds = [];
        var seenIds = {};
        for (var li = 0; li < positions.length; li++) {
          var lid = positions[li].logo.logoId;
          if (!seenIds[lid]) {
            // Round 5 §6c: numeric like logoId/variantId (string joins unchanged).
            uniqueLogoIds.push(/^\d+$/.test(String(lid)) ? Number(lid) : lid);
            seenIds[lid] = true;
          }
        }

        parentProps['_customization_data'] = JSON.stringify({
          type: 'standard_product',
          positions: buildCustomizationDataPositions(positions),
          logoIds: uniqueLogoIds,
          parentVariantId: sizeEntry.variantId,
          customizationCount: positions.length,
          timestamp: timestamp
        });

        parentProps['_has_customization'] = 'true';
        parentProps['_customization_count'] = String(positions.length);
        parentProps['_all_logo_ids'] = uniqueLogoIds.join(',');
        parentProps['_Logo Id'] = positions[0].logo.logoId; // PARENT line: underscored — an indexless base beside indexed slots is undefined for Tracker (break risk #3); control has no bare Logo Id here

        addNumberedProperties(parentProps, positions, config.categories);
        parentProps['_system_version'] = 'v3';
        // Row-scope id (2026-08-02, ports control's 2026-07-23 fix): every line
        // of ONE standard add (parent + all its children, every size) carries
        // the same hidden _customization_instance_id, written in the add
        // payload itself. It is the authoritative cart-row scope when the same
        // variant is added twice with different logos — unlike _ParentKey it
        // cannot be lost (no post-add write), unlike item.key it survives
        // quantity-change key churn. Mirrors the bundle _bundle_instance_id.
        if (config.instanceId) { parentProps['_customization_instance_id'] = String(config.instanceId); }
        // Doc 10 P1 — parent provenance block (variant sources, flow, session id,
        // build, device). PARENT-only → no child / _bundle_raw change → the bundle
        // heal byte-equivalence is untouched. Present only when the caller (confirm
        // handler) set config.provenance; the byte-locked fixtures don't, so they're
        // unchanged.
        if (config.provenance) { parentProps['_jrs_provenance'] = JSON.stringify(config.provenance); }
      }

      items.push({
        id: sizeEntry.variantId,
        quantity: sizeEntry.quantity,
        properties: parentProps
      });
    }

    if (hasCustomization) {
      // Position-major ordering (Doc 2 §10 worked-example item array shape):
      // children appear grouped by position, with sizes in input order
      // within each position. The previous size-major order (Doc 2 §10
      // doesn't mandate the order, but the fixtures lock it) interleaved
      // children differently and failed byte-identical comparison.
      for (var pi = 0; pi < positions.length; pi++) {
        var pos = positions[pi];
        for (var s2 = 0; s2 < product.sizes.length; s2++) {
          var sizeEntry2 = product.sizes[s2];
          var lineItemKey = buildLineItemKey(pos.positionVariantId, sizeEntry2.variantId, pos.logo.logoId, pos.positionName);
          var childProps = {
            '_ParentVariant': String(sizeEntry2.variantId),
            '_qty': String(sizeEntry2.quantity),
            '_time': String(timestamp),
            'Logo Id': pos.logo.logoId,
            'Position': pos.positionName,
            'Application Method': pos.applicationMethod,
            'Logo Type': mapLogoTypeToDisplay(pos.logo.type),
            '_logoVariant': String(pos.positionVariantId), // §1 (round 2): customizer flow uses the position's own variant (control parity)
            '_lineItemKey': lineItemKey,
            '_unique_key': lineItemKey,
            '_system_version': 'v3'
          };

          if (config.instanceId) { childProps['_customization_instance_id'] = String(config.instanceId); }
          // control parity (2026-08-04): customizer children carry their
          // 1-based position ordinal.
          childProps['_logoposition'] = String(pi + 1);
          // §6 — the ONE visible child property: which garment this charge
          // applies to. Key behind a constant pending Hamza's wording ruling.
          childProps[APPLIED_TO_CHILD_KEY] = _garmentLabel(product.productTitle, sizeEntry2.colour || product.colour, sizeEntry2.size);

          addChildLogoProperties(childProps, pos.logo);

          items.push({
            id: pos.positionVariantId,
            quantity: sizeEntry2.quantity,
            properties: childProps
          });
        }
      }
    }

    return { items: items };
  }

  /**
   * @param {import('./jrs-v2-types.js').ProductConfiguration} config
   * @returns {import('./jrs-v2-types.js').CartPayload}
   */
  function buildBundlePayload(config) {
    var timestamp = Date.now();
    var totalQty = getTotalQuantity(config);
    var bundleVariantId = getBundleVariantId(config);
    var hasCustomization = !!(config.customization && config.customization.positions && config.customization.positions.length > 0);
    var bundleHandle = config.bundleProductHandle || '';
    var bundleInstanceId = mintBundleInstanceId(timestamp);

    // ERP parity (2026-08-04): `_bundle` is control's bundle-preview URL
    // (/pages/bundle?bundle=<base64 minimal payload>) and `_bundle_raw` is the
    // control-shape NESTED STRUCTURE (not a JSON string) — CartAPI submits the
    // bundle parent form-encoded so Shopify stores it as a nested hash keyed
    // "0","1",… with all-string scalars, byte-shape-identical to control.
    var controlBundle = buildControlBundleStructure(config);
    // Reference v1.1 §11.2 (2026-08-20, supersedes the flat sku/quantity
    // shape): _bundle_raw is the PRODUCTION control structure — categories
    // keyed "0","1",…, products keyed Product_N, positions carrying the full
    // decoration channel, every scalar a string via form encoding. Drift
    // guarantee for §1.3: `_bundle_raw` and `_bundle_meta` are THE SAME
    // in-memory object, built once — they cannot diverge.
    var parentProps = {
      '_bundle': buildBundleUrl(controlBundle),
      '_bundle_raw': controlBundle,
      '_bundle_meta': controlBundle,
      '_bundle_instance_id': bundleInstanceId
    };

    // Bundle parent always represents one bundle instance regardless of
    // sub-product totals (§2.3 / §2.4 worked examples: parent quantity 1,
    // children carry per-line quantities). totalQty is still used below
    // for child quantities until the per-size split lands in commit 12.
    var items = [{
      id: bundleVariantId,
      quantity: 1,
      properties: parentProps
    }];

    if (!hasCustomization) {
      return { items: items };
    }

    var sortedPositions = sortPositionsByCategory(config.customization.positions);

    // Build deduplicated logo id list from all positions
    var uniqueLogoIds = [];
    var seenIds = {};
    for (var li = 0; li < sortedPositions.length; li++) {
      var lid = sortedPositions[li].logo.logoId;
      if (!seenIds[lid]) {
        // Round 5 §6c: numeric like logoId/variantId (string joins unchanged).
        uniqueLogoIds.push(/^\d+$/.test(String(lid)) ? Number(lid) : lid);
        seenIds[lid] = true;
      }
    }

    // Doc 2 §13.11 — customization triplet on parent
    parentProps['_customization_data'] = JSON.stringify({
      type: 'bundle_product',
      positions: buildCustomizationDataPositions(sortedPositions),
      logoIds: uniqueLogoIds,
      parentVariantId: bundleVariantId,
      customizationCount: sortedPositions.length,
      timestamp: timestamp
    });
    parentProps['_has_customization'] = 'true';
    parentProps['_customization_count'] = String(sortedPositions.length);
    parentProps['_all_logo_ids'] = uniqueLogoIds.join(',');
    parentProps['_Logo Id'] = sortedPositions[0].logo.logoId; // PARENT line: underscored — see note above

    addNumberedProperties(parentProps, sortedPositions, config.categories, /* isBundle */ true);
    parentProps['_system_version'] = 'v3';
    // Doc 10 P1 — parent provenance block (see standard path). Bundle PARENT only;
    // no child / _bundle_raw change → heal byte-equivalence intact.
    if (config.provenance) { parentProps['_jrs_provenance'] = JSON.stringify(config.provenance); }

    // Doc 2 §13.12 — per-size split. One child per
    // (position, applicable category, product in category, size in product).
    // The _lineItemKey depends only on (positionVariantId, bundleVariantId,
    // logoId, positionName), so all children of the same position share the
    // same key (§6.3 same-variant collision case is normal under per-size
    // split). Quantity and _qty come from the size entry, not totalQty.
    for (var pi = 0; pi < sortedPositions.length; pi++) {
      var pos = sortedPositions[pi];
      var positionLineItemKey = buildLineItemKey(pos.positionVariantId, bundleVariantId, pos.logo.logoId, pos.positionName);
      var applicableIndices = getApplicableCategoryIndices(pos, config.categories);

      for (var ai = 0; ai < applicableIndices.length; ai++) {
        var category = config.categories[applicableIndices[ai]];
        for (var pri = 0; pri < category.products.length; pri++) {
          var product = category.products[pri];
          for (var sz = 0; sz < product.sizes.length; sz++) {
            var sizeEntry = product.sizes[sz];
            if (!sizeEntry || sizeEntry.quantity <= 0) continue;

            // v8.1.3 — ONE builder for bundle child properties, shared with the cart
            // validator's heal (order #73611: the heal's own copy wrote '_Logo Id',
            // '_Position', … (hidden) and the position variant in _logoVariant).
            var childProps = buildBundleChildProperties({
              bundleVariantId: bundleVariantId,
              timestamp: timestamp,
              bundleInstanceId: bundleInstanceId,
              position: pos,
              category: category,
              product: product,
              sizeEntry: sizeEntry,
              lineItemKey: positionLineItemKey
            });

            items.push({
              id: pos.positionVariantId,
              quantity: sizeEntry.quantity,
              properties: childProps
            });
          }
        }
      }
    }

    return { items: items };
  }

  /**
   * @param {import('./jrs-v2-types.js').PositionAssignment[]} positions
   * @returns {{ position: string, applicationMethod: string, logoId: string, variantId: string }[]}
   */
  function buildCustomizationDataPositions(positions) {
    var output = [];
    for (var i = 0; i < positions.length; i++) {
      output.push({
        position: positions[i].positionName,
        applicationMethod: positions[i].applicationMethod,
        // control types: numeric logo ids and variant ids are NUMBERS
        logoId: /^\d+$/.test(String(positions[i].logo.logoId)) ? Number(positions[i].logo.logoId) : positions[i].logo.logoId,
        variantId: Number(positions[i].positionVariantId),
        // Round 5 §6b: discriminator — two same-position/same-method entries
        // from different categories were byte-identical (_jrs_provenance's
        // "[cat N]" suffix solved this; mirror it structurally here).
        categoryIndex: Number(positions[i].categoryIndex) || 0
      });
    }
    return output;
  }

  /**
   * @param {import('./jrs-v2-types.js').PositionAssignment[]} positions
   * @returns {import('./jrs-v2-types.js').PositionAssignment[]}
   */
  function sortPositionsByCategory(positions) {
    var indexed = [];
    for (var i = 0; i < positions.length; i++) {
      indexed.push({ idx: i, pos: positions[i] });
    }
    indexed.sort(function(a, b) {
      if (a.pos.categoryIndex === b.pos.categoryIndex) return a.idx - b.idx;
      return a.pos.categoryIndex - b.pos.categoryIndex;
    });

    var sorted = [];
    for (var j = 0; j < indexed.length; j++) {
      sorted.push(indexed[j].pos);
    }
    return sorted;
  }

  /**
   * @param {Object<string, string|number>} props
   * @param {import('./jrs-v2-types.js').PositionAssignment[]} positions
   * @param {import('./jrs-v2-types.js').CategoryConfiguration[]} categories
   */
  function addNumberedProperties(props, positions, categories, isBundle) {
    for (var i = 0; i < positions.length; i++) {
      var pos = positions[i];
      var n = i + 1;

      var category = categories[pos.categoryIndex];
      // §5 (2026-08-04): control emits _logoproduct/_logoposition N on EVERY
      // bundle parent (incl. single-category bundles); multistep keeps the
      // multi-category-only gate (never true today).
      // Round 5 §6a: a bundle-wide position spans every category — naming
      // category 0 alone misleads an ERP reading the numbered props. Mirror
      // Applied To N's scope exactly ("All garments" for multi-category
      // bundle-wide; the trimmed category name otherwise — D, byte-exact).
      var _appliesAll = !!(pos.sourceLogoGroupId && /^bundlewide-/.test(pos.sourceLogoGroupId));
      if ((isBundle || categories.length > 1) && category) {
        props['_logoproduct' + n] = (_appliesAll && categories.length > 1)
          ? 'All garments'
          : String(category.name || '').trim();
        props['_logoposition' + n] = pos.positionName;
      }

      // Slot labelling (2026-08-02, additive) — BUNDLE parents only: name the
      // garment each numbered slot applies to, emitted immediately before
      // "Position N" so checkout renders it first in the slot group. Value
      // source = the same category display name that feeds _logoproduct N. A
      // bundle-wide position applies to EVERY category (Doc 2 §6), so naming
      // one garment would mislead — it reads "All garments" instead (a
      // single-category bundle keeps its one real name; nothing to mislead).
      // Multistep single-product parents are one garment — skipped.
      if (isBundle && category) {
        props['Applied To ' + n] = (_appliesAll && categories.length > 1)
          ? 'All garments'
          : String(category.name || '').trim();
      }

      // Reference v1.1 §4.1 correction (2026-08-20): control's PRODUCTION
      // form is "<BaseName> <n>" WITH the space and no underscore — the
      // no-space form from the v1.0 prose was never production-observed.
      // "Logo Id"/"Logo"/"Logo Type" remain customer-visible in hosted
      // checkout (matches live control); theme cart surfaces suppress them.
      props['Position ' + n] = pos.positionName;
      props['Application Method ' + n] = pos.applicationMethod;
      props['Logo Type ' + n] = mapLogoTypeToDisplay(pos.logo.type);
      props['Logo Id ' + n] = pos.logo.logoId;
      // §1 (2026-08-04, round 2): the logo-PRODUCT variant applies on the
      // BUNDLE flow only; control's customizer flow carries the position's own
      // variant here. Position variant stays in _customization_data either way.
      props['_logoVariant' + n] = isBundle
        ? (logoProductVariantFor(mapLogoTypeToDisplay(pos.logo.type)) || String(pos.positionVariantId))
        : String(pos.positionVariantId);

      if (pos.logo.type === 'upload' || pos.logo.type === 'existing') {
        props['Logo ' + n] = pos.logo.url || '';
        // Doc 2 §13.8 — Logo Filename N on parent when filename is recoverable.
        if (pos.logo.filename) {
          props['Logo Filename ' + n] = pos.logo.filename;
        }
      }

      // Doc 2 §13.10 — omit empty text-logo properties.
      if (pos.logo.type === 'text' && pos.logo.text) {
        var lines = pos.logo.text.lines || [];
        if (lines[0]) props['Logo Text ' + n] = lines[0];
        if (lines[1]) props['_Logo Text 2 ' + n] = lines[1];
        if (lines[2]) props['_Logo Text 3 ' + n] = lines[2];
        if (lines[3]) props['_Logo Text 4 ' + n] = lines[3];
        if (pos.logo.text.colour) props['_Logo Color ' + n] = pos.logo.text.colour;
        if (pos.logo.text.font) props['_Logo Font ' + n] = pos.logo.text.font;
      }

      // §3 (2026-08-04): visible customer note, ALWAYS emitted (control
      // carries 'Note 1: ' even when empty) — production reads it.
      props['Note ' + n] = pos.logo.note || '';
    }
  }

  /**
   * @param {Object<string, string|number>} props
   * @param {import('./jrs-v2-types.js').LogoConfiguration} logo
   */
  /**
   * v8.1.3 — THE bundle child property set. Byte-identical to what buildBundlePayload
   * emitted before this refactor; now also the only source the cart validator's
   * missing-child heal uses, so a reconstructed line can never drift from a normally
   * added one again.
   *
   * @param {{ bundleVariantId:number, timestamp:number|string, bundleInstanceId:string,
   *           position:Object, category:Object, product:Object, sizeEntry:Object,
   *           lineItemKey?:string, logoVariantId?:string, timeStr?:string }} a
   * @returns {Object<string,string>}
   */
  function buildBundleChildProperties(a) {
    var pos = a.position;
    var logoTypeDisplay = mapLogoTypeToDisplay(pos.logo.type);
    var lineItemKey = a.lineItemKey || buildLineItemKey(pos.positionVariantId, a.bundleVariantId, pos.logo.logoId, pos.positionName);
    var enrichmentProps = buildBundleChildCategoryProps(a.category, a.product, a.sizeEntry);
    var childProps = {
      '_ParentVariant': String(a.bundleVariantId),
      '_qty': String(a.sizeEntry.quantity),
      '_time': String(a.timeStr || a.timestamp),
      'Logo Id': pos.logo.logoId,
      'Position': pos.positionName,
      'Application Method': pos.applicationMethod,
      'Logo Type': logoTypeDisplay,
      // logo PRODUCT variant (control semantics) — the heal passes the value stored in
      // _bundle_raw.positions[].logoVariantId; the live add resolves it from the page map.
      '_logoVariant': String(a.logoVariantId || logoProductVariantFor(logoTypeDisplay) || pos.positionVariantId),
      '_lineItemKey': lineItemKey,
      '_unique_key': lineItemKey,
      '_bundle_instance_id': a.bundleInstanceId,
      '_system_version': 'v3'
    };
    for (var ek in enrichmentProps) {
      if (enrichmentProps.hasOwnProperty(ek)) childProps[ek] = enrichmentProps[ek];
    }
    // §6 — the ONE visible child property (see APPLIED_TO_CHILD_KEY).
    childProps[APPLIED_TO_CHILD_KEY] = _garmentLabel(a.product.productTitle, a.sizeEntry.colour || a.product.colour, a.sizeEntry.size);
    addChildLogoProperties(childProps, pos.logo);
    return childProps;
  }

  /**
   * v8.1.3 — THE multistep child property set (mirrors buildStandardPayload's child
   * emission exactly). Used by the validator's multistep heal.
   * @param {{ parentVariantId:number, quantity:number, timestamp:number|string, position:Object,
   *           positionIndex:number, instanceId?:string, product:Object, sizeEntry:Object,
   *           lineItemKey?:string }} a
   */
  function buildMultistepChildProperties(a) {
    var pos = a.position;
    var lineItemKey = a.lineItemKey || buildLineItemKey(pos.positionVariantId, a.parentVariantId, pos.logo.logoId, pos.positionName);
    var childProps = {
      '_ParentVariant': String(a.parentVariantId),
      '_qty': String(a.quantity),
      '_time': String(a.timestamp),
      'Logo Id': pos.logo.logoId,
      'Position': pos.positionName,
      'Application Method': pos.applicationMethod,
      'Logo Type': mapLogoTypeToDisplay(pos.logo.type),
      '_logoVariant': String(pos.positionVariantId), // §1 (round 2): customizer flow uses the position's own variant (control parity)
      '_lineItemKey': lineItemKey,
      '_unique_key': lineItemKey,
      '_system_version': 'v3'
    };
    if (a.instanceId) { childProps['_customization_instance_id'] = String(a.instanceId); }
    childProps['_logoposition'] = String(a.positionIndex + 1);
    childProps[APPLIED_TO_CHILD_KEY] = _garmentLabel(a.product.productTitle, a.sizeEntry.colour || a.product.colour, a.sizeEntry.size);
    addChildLogoProperties(childProps, pos.logo);
    if (pos.logo && pos.logo.note) childProps['_Note'] = String(pos.logo.note);
    return childProps;
  }

  function addChildLogoProperties(props, logo) {
    if (logo.type === 'upload' || logo.type === 'existing') {
      props['Logo'] = logo.url || '';
      if (logo.type === 'upload' && logo.filename) {
        props['Logo Filename'] = logo.filename;
      }
    }

    // Doc 2 §13.10 — omit empty text-logo properties (applies to children
    // symmetrically with the parent-side gates in addNumberedProperties).
    if (logo.type === 'text' && logo.text) {
      var lines = logo.text.lines || [];
      if (lines[0]) props['_Logo Text'] = lines[0];
      if (lines[1]) props['_Logo Text 2'] = lines[1];
      if (lines[2]) props['_Logo Text 3'] = lines[2];
      if (lines[3]) props['_Logo Text 4'] = lines[3];
      if (logo.text.colour) props['_Logo Color'] = logo.text.colour;
      if (logo.text.font) props['_Logo Font'] = logo.text.font;
    }
  }

  /**
   * Resolve which category indices a position applies to.
   *
   * Per Doc 2 §6 / §13.12: a position whose `sourceLogoGroupId` matches the
   * `bundlewide-*` pattern applies to every category in the bundle (Step 0A
   * worked example: one logo placed bundle-wide). Otherwise the position is
   * scoped to a single category via `pos.categoryIndex` (Step 0B worked
   * example: per-category logo selections).
   *
   * @param {import('./jrs-v2-types.js').PositionAssignment} pos
   * @param {import('./jrs-v2-types.js').CategoryConfiguration[]} categories
   * @returns {number[]} sorted array of category indices the position writes to
   */
  function getApplicableCategoryIndices(pos, categories) {
    var isBundleWide = !!(pos.sourceLogoGroupId && /^bundlewide-/.test(pos.sourceLogoGroupId));
    if (isBundleWide) {
      var out = [];
      for (var i = 0; i < categories.length; i++) {
        out.push(categories[i].index);
      }
      return out;
    }
    return [pos.categoryIndex];
  }

  /**
   * Build the nine Doc 2 §6 bundle-child enrichment properties for a single
   * (category, product, size) triple. Doc 2 §13.12 mandates one child per
   * (position, sub-product, size); `_ProductVariantId`, `_ProductSku` and
   * `_ProductSize` therefore vary per-size and must come from the size entry,
   * not the product-level fields (which represent the first/representative
   * size only).
   *
   * Returned property keys (Doc 2 §6):
   *   _BundleItemName, _BundleCategoryName, _BundleCategoryIndex,
   *   _BundleCategoryProductCount, _BundleCategoryProducts,
   *   _ProductVariantId, _ProductSku, _ProductSize, _ProductColor
   *
   * @param {import('./jrs-v2-types.js').CategoryConfiguration} category
   * @param {import('./jrs-v2-types.js').BundleProduct} product
   * @param {import('./jrs-v2-types.js').SizeEntry} sizeEntry
   * @returns {Object<string, string>}
   */
  function buildBundleChildCategoryProps(category, product, sizeEntry) {
    var handles = [];
    for (var i = 0; i < category.products.length; i++) {
      handles.push(category.products[i].productHandle);
    }

    return {
      // Round 5 §4: control's _BundleItemName is the PRODUCT title (the round-2
      // §4 category-name change was scoped too widely — it also broke the
      // summary's roster-name matching). _BundleCategoryName stays the
      // category label (v3-only, correct as-is).
      '_BundleItemName': String(product.productTitle || '').replace(/\s+/g, ' ').trim() || String(category.name || '').trim(),
      '_BundleCategoryName': String(category.name || '').trim(),
      '_BundleCategoryIndex': String(category.index),
      '_BundleCategoryProductCount': String(category.products.length),
      '_BundleCategoryProducts': handles.join(','),
      '_ProductVariantId': String(sizeEntry.variantId),
      '_ProductSku': sizeEntry.sku || '',
      '_ProductSize': sizeEntry.size || '',
      // Per-size colour, like the three fields above. A category can mix
      // colours across sizes (e.g. Black XS + Bottle Green S/M), so prefer the
      // size entry's own colour; fall back to the product's representative
      // colour for single-colour categories (and for callers that don't carry
      // per-size colour — keeps the byte-locked §2 fixtures unchanged).
      '_ProductColor': sizeEntry.colour || product.colour || ''
    };
  }

  /**
   * @param {string} type
   * @returns {string}
   */
  function mapLogoTypeToDisplay(type) {
    if (type === 'upload') return LOGO_TYPE_DISPLAY.UPLOAD;
    if (type === 'existing') return LOGO_TYPE_DISPLAY.EXISTING;
    if (type === 'text') return LOGO_TYPE_DISPLAY.TEXT;
    return LOGO_TYPE_DISPLAY.UPLOAD;
  }

  /**
   * @param {number} positionVariantId
   * @param {number} parentVariantId
   * @param {string} logoId
   * @param {string} positionName
   * @returns {string}
   */
  function buildLineItemKey(positionVariantId, parentVariantId, logoId, positionName) {
    var safePosition = String(positionName || '').replace(/\s+/g, '_');
    return positionVariantId + '_' + parentVariantId + '_' + logoId + '_' + safePosition;
  }

  // ==========================================================================
  // ERP payload parity (2026-08-04) — control-format _bundle_raw + _bundle.
  // The ERP parses the CONTROL theme's order payload; these builders replicate
  // control's mechanisms (betterconversions-bundle.js v6.2.1) rather than the
  // v2 shapes. The structure is submitted as a NESTED OBJECT (CartAPI
  // form-encodes it, so Shopify stores a nested hash with all-string scalars,
  // exactly like control's jQuery form POST).
  // ==========================================================================

  /** Logo-type display name → logo PRODUCT variant id, bridged from the
      logo_type metaobjects by jrs-customizer-popup-v2.liquid (same source as
      control's radio `variant` attributes). Cached; {} when tag absent. */
  var _logoTypeVariants;
  function _readLogoTypeVariants() {
    if (typeof _logoTypeVariants !== 'undefined') return _logoTypeVariants;
    _logoTypeVariants = {};
    try {
      if (typeof document !== 'undefined' && document.querySelector) {
        var el = document.querySelector('[data-jrs-logo-type-variants]');
        if (el && el.textContent) _logoTypeVariants = JSON.parse(el.textContent) || {};
      }
    } catch (e) { /* absent/malformed → empty map */ }
    return _logoTypeVariants;
  }

  /** The logo PRODUCT variant for a logo type display name ('' if unmapped). */
  function logoProductVariantFor(logoTypeDisplay) {
    var map = _readLogoTypeVariants();
    var entry = map && map[logoTypeDisplay];
    return entry && entry.variantId ? String(entry.variantId) : '';
  }

  /**
   * Control-shape bundle structure (the value of `_bundle_raw`):
   *   [ { name: <category name>, categoryIndex: Number,
   *       products: { Product_1: {name,color,size,quantity,sku,variantId,productId}, … },
   *       positions: [ itemObj, … ] } ]
   * One Product_N per (product,size) row with qty > 0 (control enumerates its
   * size inputs the same way). Position itemObj replicates control's
   * BUG-BUNDLE-RAW-001 standardized field set including the nested logo{}.
   */
  function buildControlBundleStructure(config) {
    var out = [];
    for (var ci = 0; ci < config.categories.length; ci++) {
      var cat = config.categories[ci];
      // D (2026-08-04): control trims the category name here (bundle builder
      // line 5634 does ($(this).attr('bundleitemName') || '').trim()).
      var bundleObject = { name: String(cat.name || '').trim(), products: {}, categoryIndex: Number(cat.index) };

      var pIndex = 1;
      var categoryQty = 0;
      for (var pi = 0; pi < cat.products.length; pi++) {
        var prod = cat.products[pi];
        for (var si = 0; si < prod.sizes.length; si++) {
          var se = prod.sizes[si];
          if (!se || !(se.quantity > 0)) continue;
          categoryQty += Number(se.quantity) || 0;
          var productObj = {
            name: prod.productTitle,
            color: se.colour || prod.colour || '',
            size: se.size || '',
            quantity: Number(se.quantity) || 0,
            sku: se.sku || ''
          };
          if (se.variantId) productObj.variantId = String(se.variantId);
          if (prod.productId) productObj.productId = String(prod.productId);
          bundleObject.products['Product_' + pIndex] = productObj;
          pIndex++;
        }
      }

      var positions = [];
      if (config.customization && config.customization.positions) {
        var posIndex = 0;
        for (var cpi = 0; cpi < config.customization.positions.length; cpi++) {
          var pos = config.customization.positions[cpi];
          if (getApplicableCategoryIndices(pos, config.categories).indexOf(cat.index) === -1) continue;
          var logoTypeDisplay = mapLogoTypeToDisplay(pos.logo.type);
          var itemObj = {
            name: pos.positionName,
            qty: categoryQty,
            bundleName: String(cat.name || '').trim(),
            applicationMethod: pos.applicationMethod,
            logoType: logoTypeDisplay,
            'Logo Id': String(pos.logo.logoId)
          };
          var logoVariant = logoProductVariantFor(logoTypeDisplay);
          if (logoVariant) itemObj.logoVariantId = logoVariant;
          if (pos.positionVariantId) {
            itemObj.positionVariantId = String(pos.positionVariantId);
            if (pos.applicationMethod === 'Embroidery') {
              itemObj.embroideryVariantId = String(pos.positionVariantId);
            } else {
              itemObj.printVariantId = String(pos.positionVariantId);
            }
          }
          if (pos.logo.type === 'text' && pos.logo.text) {
            var tl = pos.logo.text.lines || [];
            if (tl[0]) itemObj['Logo Text'] = tl[0];
            if (tl[1]) itemObj['Logo Text 2'] = tl[1];
            if (tl[2]) itemObj['Logo Text 3'] = tl[2];
            if (tl[3]) itemObj['Logo Text 4'] = tl[3];
            if (pos.logo.text.colour) itemObj['Logo Color'] = pos.logo.text.colour;
            if (pos.logo.text.font) itemObj['Logo Font'] = pos.logo.text.font;
          }
          if (pos.logo.url) itemObj['Logo'] = pos.logo.url;
          if (pos.logo.note) itemObj['Note'] = pos.logo.note;
          itemObj.categoryIndex = Number(cat.index);
          itemObj.positionIndex = Number(posIndex);
          if (pos.logo.url && pos.logo.filename) itemObj['Logo Filename'] = pos.logo.filename;

          // Nested logo{} — control's logoObj, incl. the Order #52354 text
          // normalization (color/font always present for Text Logo).
          var logoObj = {};
          if (itemObj['Logo Id']) logoObj.logoId = itemObj['Logo Id'];
          if (itemObj.logoType) logoObj.logoType = itemObj.logoType;
          if (itemObj.applicationMethod) logoObj.applicationMethod = itemObj.applicationMethod;
          if (itemObj['Logo Text']) logoObj.text1 = itemObj['Logo Text'];
          if (itemObj['Logo Text 2']) logoObj.text2 = itemObj['Logo Text 2'];
          if (itemObj['Logo Text 3']) logoObj.text3 = itemObj['Logo Text 3'];
          if (itemObj['Logo Text 4']) logoObj.text4 = itemObj['Logo Text 4'];
          if (itemObj['Logo Color']) logoObj.color = itemObj['Logo Color'];
          if (itemObj['Logo Font']) logoObj.font = itemObj['Logo Font'];
          if (itemObj.logoType === 'Text Logo') {
            logoObj.color = itemObj['Logo Color'] || logoObj.color || '';
            logoObj.font = itemObj['Logo Font'] || logoObj.font || '';
          }
          if (itemObj['Logo']) {
            logoObj.url = itemObj['Logo'];
            if (itemObj['Logo Filename']) logoObj.filename = itemObj['Logo Filename'];
          }
          if (itemObj['Note']) logoObj.note = itemObj['Note'];
          if (Object.keys(logoObj).length > 0) itemObj.logo = logoObj;

          positions.push(itemObj);
          posIndex++;
        }
      }
      bundleObject.positions = positions;
      out.push(bundleObject);
    }
    return out;
  }

  /**
   * Control's `_bundle` URL algorithm (bundle order preview link):
   * minimal payload → ultra-minimal → base64 → raw-JSON → hash+_bundle_raw
   * fallbacks, with control's exact thresholds. Operates on the control-shape
   * bundle structure above.
   */
  function buildBundleUrl(bundle) {
    var minimalBundle = bundle.map(function (bundleItem) {
      var minimal = { name: bundleItem.name, products: {} };
      if (bundleItem.products) {
        Object.keys(bundleItem.products).forEach(function (key) {
          var product = bundleItem.products[key];
          minimal.products[key] = {
            name: product.name, color: product.color, size: product.size,
            quantity: product.quantity, sku: product.sku
          };
        });
      }
      var positionsArray = Array.isArray(bundleItem.positions) ? bundleItem.positions : [];
      if (positionsArray.length > 0) {
        minimal.positions = positionsArray.map(function (pos) {
          var minimalPos = {
            name: pos.name,
            qty: pos.qty || 1,
            bundleName: pos.bundleName || bundleItem.name,
            'Application Method': pos.applicationMethod || 'Embroidery',
            logo_type: pos.logoType || ''
          };
          if (pos['Logo Id']) minimalPos['Logo Id'] = pos['Logo Id'];
          if (pos.logoVariantId) minimalPos.logoVariantId = pos.logoVariantId;
          if (pos.embroideryVariantId) minimalPos.embroideryVariantId = pos.embroideryVariantId;
          if (pos.printVariantId) minimalPos.printVariantId = pos.printVariantId;
          if (pos.positionVariantId) minimalPos.positionVariantId = pos.positionVariantId;
          return minimalPos;
        });
      }
      return minimal;
    });

    var jsonString = JSON.stringify(minimalBundle);
    var baseUrlLength = 'https://jrsindustrial.co.uk/pages/bundle?bundle='.length;
    var estimatedEncodedLength = baseUrlLength + (jsonString.length * 1.6);
    var mrkPage_url = '';

    if (estimatedEncodedLength > 1800 || jsonString.length > 1100) {
      var ultraMinimalBundle = minimalBundle.map(function (bundleItem) {
        var ultraMinimal = { name: bundleItem.name, products: bundleItem.products };
        if (bundleItem.positions && bundleItem.positions.length > 0) {
          ultraMinimal.positions = bundleItem.positions.map(function (pos) {
            return {
              name: pos.name,
              qty: pos.qty || 1,
              bundleName: pos.bundleName || bundleItem.name,
              'Application Method': pos['Application Method'] || 'Embroidery',
              logo_type: pos.logo_type || '',
              'Logo Id': pos['Logo Id'] || ''
            };
          });
        }
        return ultraMinimal;
      });
      var ultraMinimalJson = JSON.stringify(ultraMinimalBundle);
      var ultraLen = baseUrlLength + (ultraMinimalJson.length * 1.6);
      if (ultraLen > 1800 || ultraMinimalJson.length > 1100) {
        // Hash placeholder + base64 _bundle_raw param (control's rare fallback).
        var hashSource = '';
        if (bundle.length > 0 && bundle[0].name) hashSource = bundle[0].name.substring(0, 20);
        if (bundle.length > 0 && bundle[0].products) {
          var fpk = Object.keys(bundle[0].products)[0];
          if (fpk && bundle[0].products[fpk]) hashSource += bundle[0].products[fpk].sku || '';
        }
        var hash = 0;
        for (var i = 0; i < hashSource.length; i++) {
          hash = ((hash << 5) - hash) + hashSource.charCodeAt(i);
          hash = hash & hash;
        }
        var bundleHash = Math.abs(hash).toString(36).substring(0, 8) || 'bundle';
        var bundleRawString = '';
        try {
          var bundleRawJson = JSON.stringify(bundle);
          var base64Raw = btoa(unescape(encodeURIComponent(bundleRawJson)));
          var base64Url = 'https://jrsindustrial.co.uk/pages/bundle?bundle=' + bundleHash + '&from=order&_bundle_raw=' + encodeURIComponent(base64Raw);
          if (base64Url.length <= 8000) bundleRawString = encodeURIComponent(base64Raw);
          else bundleRawString = encodeURIComponent(base64Raw);
        } catch (e) { bundleRawString = ''; }
        if (bundleRawString) {
          var finalUrl = 'https://jrsindustrial.co.uk/pages/bundle?bundle=' + bundleHash + '&from=order&_bundle_raw=' + bundleRawString;
          mrkPage_url = finalUrl.length <= 8000
            ? finalUrl
            : 'https://jrsindustrial.co.uk/pages/bundle?bundle=' + bundleHash + '&from=order';
        } else {
          mrkPage_url = 'https://jrsindustrial.co.uk/pages/bundle?bundle=' + bundleHash + '&from=order';
        }
        return mrkPage_url;
      }
      jsonString = ultraMinimalJson;
    }

    var base64String = '';
    try {
      base64String = btoa(unescape(encodeURIComponent(jsonString)));
    } catch (e) {
      base64String = encodeURIComponent(jsonString);
    }
    mrkPage_url = 'https://jrsindustrial.co.uk/pages/bundle?bundle=' + encodeURIComponent(base64String);
    if (mrkPage_url.length > 1900) {
      var rawJsonUrl = 'https://jrsindustrial.co.uk/pages/bundle?bundle=' + encodeURIComponent(jsonString);
      if (rawJsonUrl.length <= 1900) mrkPage_url = rawJsonUrl;
    }
    return mrkPage_url;
  }

  /**
   * @param {import('./jrs-v2-types.js').ProductConfiguration} config
   * @returns {Array}
   */
  function buildBundleRawFromConfig(config) {
    var out = [];

    for (var ci = 0; ci < config.categories.length; ci++) {
      var cat = config.categories[ci];
      var productsObj = {};

      for (var pi = 0; pi < cat.products.length; pi++) {
        var p = cat.products[pi];
        var sizesObj = {};
        for (var si = 0; si < p.sizes.length; si++) {
          // Doc 2 §9.1 v0.5 — per-size {qty, variantId, sku} (was {size: qty}).
          // Makes the bundle parent a COMPLETE witness so the validator can
          // reconstruct a dropped child byte-exact (per-size _ProductVariantId /
          // _ProductSku are otherwise unrecoverable). See docs §9 change log.
          // v0.5.2 — key by variantId, NOT the size string. A category can hold two
          // garments of the same size but different variant (e.g. two colours); a
          // size-keyed map collapses them and one vanishes from the witness, so the
          // validator can neither detect nor heal its dropped branding child. variantId
          // is 1:1 with size, so no size is lost; `size` is carried in the value for
          // reconstruction (and back-compat with pre-v0.5.2 size-keyed carts).
          sizesObj[String(p.sizes[si].variantId)] = {
            size: p.sizes[si].size,
            qty: p.sizes[si].quantity,
            variantId: p.sizes[si].variantId,
            sku: p.sizes[si].sku || ''
          };
        }

        productsObj[p.productHandle] = {
          name: p.productTitle,
          handle: p.productHandle,
          product_id: p.productId,
          variant_id: p.variantId,
          color: p.colour,
          sizes: sizesObj,
          image: p.imageUrl || '',
          price: 0
        };
      }

      var catPositions = [];
      if (config.customization && config.customization.positions) {
        for (var cpi = 0; cpi < config.customization.positions.length; cpi++) {
          var pos = config.customization.positions[cpi];
          // v0.5.1 — include a position in EVERY category it APPLIES to, not just its
          // home pos.categoryIndex. A bundle-wide position (sourceLogoGroupId
          // "bundlewide-*") applies to ALL categories, and buildBundlePayload emits a
          // child for each (applicable category × product × size) — see line ~302–305.
          // _bundle_raw must encode that same fan-out so the validator heal
          // (_buildMissingBundleChildren) reconstructs the correct COUNT of per-garment
          // children across every category, not just the home one (the malformed-heal
          // bug: a bundle-wide position dropped across N garments healed to 1 line).
          // Mirrors getApplicableCategoryIndices EXACTLY (same call as line 302).
          if (getApplicableCategoryIndices(pos, config.categories).indexOf(cat.index) === -1) continue;

          var logoTypeDisplay = mapLogoTypeToDisplay(pos.logo.type);
          var posObj = {
            name: pos.positionName,
            logoVariantId: pos.positionVariantId,
            'Logo Id': pos.logo.logoId,
            'Logo': pos.logo.url || '',
            'Logo Type': logoTypeDisplay,
            'Application Method': pos.applicationMethod,
            Note: pos.logo.note || ''
          };

          if (pos.logo.type === 'text' && pos.logo.text) {
            posObj['Logo Text'] = pos.logo.text.lines[0] || '';
            posObj['Logo Text 2'] = pos.logo.text.lines[1] || '';
            posObj['Logo Text 3'] = pos.logo.text.lines[2] || '';
            posObj['Logo Text 4'] = pos.logo.text.lines[3] || '';
            posObj['Logo Color'] = pos.logo.text.colour || '';
            posObj['Logo Font'] = pos.logo.text.font || '';
          }

          catPositions.push(posObj);
        }
      }

      out.push({
        name: cat.name,
        categoryIndex: cat.index,
        products: productsObj,
        positions: catPositions
      });
    }

    return out;
  }

  /**
   * @param {import('./jrs-v2-types.js').ProductConfiguration} config
   * @returns {number}
   */
  function getTotalQuantity(config) {
    var total = 0;
    for (var ci = 0; ci < config.categories.length; ci++) {
      var products = config.categories[ci].products;
      for (var pi = 0; pi < products.length; pi++) {
        var sizes = products[pi].sizes;
        for (var si = 0; si < sizes.length; si++) {
          total += sizes[si].quantity;
        }
      }
    }
    return total;
  }

  /**
   * Bundle parent variant id resolution.
   *
   * Per Doc 2 §13.13 (v0.4), the canonical source is the ProductConfiguration's
   * `bundleProductVariantId` field, populated by the bundle PDP Liquid template
   * from `product.selected_or_first_available_variant.id` at popup mount time.
   *
   * Fallback: when the field is missing (in-flight bundle configurations from
   * Phase 1 autosaved drafts), fall back to the legacy "first sub-product
   * variant" behaviour and log a warning. The fallback preserves Phase 1 cart
   * resumption while the new field rolls out; Phase 2+ step controllers always
   * populate it at submission.
   *
   * @param {import('./jrs-v2-types.js').ProductConfiguration} config
   * @returns {number}
   */
  function getBundleVariantId(config) {
    if (typeof config.bundleProductVariantId === 'number' && config.bundleProductVariantId > 0) {
      return config.bundleProductVariantId;
    }

    if (window.console && console.warn) {
      console.warn(
        'CartPayloadBuilder: bundle configuration missing bundleProductVariantId — ' +
        'falling back to first sub-product variant (legacy behaviour, Phase 1 resumption only). ' +
        'See Doc 2 §13.13.'
      );
    }

    if (config.categories.length > 0 && config.categories[0].products.length > 0) {
      return config.categories[0].products[0].variantId;
    }
    throw new Error('CartPayloadBuilder: Cannot determine bundle variant ID - no products in first category');
  }

  /**
   * Mint a per-submission bundle instance id per Doc 2 §13.6.
   *
   * Format: `bundle-${timestamp}-${randomShortString}`. The timestamp ties
   * the bundle to its submission `_time`. The random suffix prevents
   * collision when two bundles are submitted in the same millisecond
   * (theoretical; defence-in-depth).
   *
   * @param {number} timestamp - the submission's Date.now() value, reused
   *                              from the caller so all per-submission
   *                              properties share the same timestamp.
   * @returns {string}
   */
  function mintBundleInstanceId(timestamp) {
    var randomShortString = Math.random().toString(36).slice(2, 8);
    return 'bundle-' + String(timestamp) + '-' + randomShortString;
  }

  JRS.CartPayloadBuilder = {
    build: build,
    validate: validate,
    // v8.1.3 — shared child-property builders (used by the cart validator's heal)
    buildBundleChildProperties: buildBundleChildProperties,
    buildMultistepChildProperties: buildMultistepChildProperties,
    mapLogoTypeToDisplay: mapLogoTypeToDisplay
  };
})(window.JRS);
